import { JsonServiceClient, ServerEventsClient } from '@servicestack/client';
import AuthStore from 'bz-stores/lib/auth-store';
import { LicenseObservable } from 'bz-stores/lib/licence';
import { TimeUnit } from 'bz-utilities/global/date';
import { createContextForClass } from 'bz-utilities/hooks/context-v2';
import _ from 'lodash';
import type { IObservableArray } from 'mobx';
import { action, computed, observable, runInAction } from 'mobx';
import type { Terminal, TerminalPaymentDto } from 'tm3-core/api.dtos';
import {
  CancelTerminalCheckoutRequest,
  PaymentProviderPaymentStatus,
  StartTerminalPaymentRequest,
  TerminalListRequest,
  TerminalPaymentProviderPaymentStatus,
  TerminalPaymentStatusRequest
} from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';
import z from 'z';

class TerminalUpdateMessage {
  // not auto-generated in dtos sadly
  message: string = '';
}

export type TClickTerminalDropdownItems = {
  id: Terminal['merchantId'];
  value: Terminal['name'];
  name: Terminal['name'];
};

export type TTerminalDropdownItems = { payload: Terminal['merchantId']; text: Terminal['name'] };

class TerminalPaymentDialogStore {
  static readonly POLLING_INTERVAL: number = 10 * TimeUnit.second;
  static readonly FAIL_TIMER = 5 * TimeUnit.minute;

  @observable transactionAmount: number = 0;
  @observable selectedTerminalId: string | null = null;
  @observable customerId: number = 0;
  @observable dialogOpen: boolean = false;
  @observable showUnloadWarning: boolean = false;

  @observable loading: boolean = true;
  @observable loaded: boolean = false;
  @observable paymentError: boolean = false;
  @observable errorMessage: string = '';
  @observable checkoutId: string | null = null;
  @observable terminals: IObservableArray<Terminal> = observable.array([]);
  @observable processingStatusMessage = 'Starting Communication with Terminal';
  @observable isProcessing = false;
  @observable locationId: number = 0;
  @observable terminalPaymentSuccessful: boolean = false;

  serverEventsClient!: ServerEventsClient;
  terminalPaymentDto?: TerminalPaymentDto;
  client: JsonServiceClient;
  interval?: number;
  failTimeout?: number;
  successCallback?: (terminalPaymentDto: TerminalPaymentDto, incomeId?: number) => void;
  onCloseCallback?: () => void;
  constructor() {
    this.client = new JsonServiceClient('/api');
  }

  @computed
  get hasTerminals() {
    return this.terminals && this.terminals.length > 0; // TODO
  }

  @computed
  get selectedTerminalName(): string {
    if (!this.selectedTerminalId) {
      return '';
    }
    return this.terminals.find((x) => x.merchantId === this.selectedTerminalId)?.name ?? '';
  }

  @computed
  get currentTerminals(): Terminal[] {
    return this.terminals.filter((x) => x.locationId === AuthStore.user?.location?.id);
  }

  @computed
  get clickTerminalDropdownItems(): TClickTerminalDropdownItems[] {
    return this.currentTerminals.map((t) => ({ id: t.merchantId, value: t.name, name: t.name }));
  }

  @computed
  get terminalDropdownItems(): TTerminalDropdownItems[] {
    return this.currentTerminals.map((t) => ({ payload: t.merchantId, text: t.name }));
  }

  @action.bound
  async load(): Promise<void> {
    const req = new TerminalListRequest();
    try {
      let res = await this.client.get(req);
      runInAction(() => {
        this.terminals.replace(res.result);
        this.loading = false;
        this.loaded = true;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  @action
  beforeUnloadWarning(e: any) {
    window.setTimeout(() => (this.showUnloadWarning = true), 10);
    const confirmationMessage = z.format(
      'It looks like you have a terminal payment in progress. ' +
        'If you leave before saving, a payment might be taken, but {TM3} will have no record of it.'
    );
    e.preventDefault();
    (e || window.event).returnValue = confirmationMessage; //Gecko + IE
    return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
  }

  @action.bound
  openDialog(
    amount: number,
    customerId: number,
    locationId: number,
    successCallback: (terminalPaymentDto: TerminalPaymentDto) => void,
    terminalId?: string,
    onCloseCallback?: () => void
  ): void {
    if (!amount || amount <= 0 || !customerId || customerId <= 0) {
      return;
    }

    this.dialogOpen = true;
    this.transactionAmount = amount;
    this.customerId = customerId;
    this.locationId = locationId;

    terminalId ??= this.terminals.find(
      (x) => x.locationId === AuthStore.user?.location?.id
    )?.merchantId;

    if (!terminalId) {
      this.printError('openDialog', 'Terminal Payment Error: TerminalId is null');
      return;
    }

    this.selectedTerminalId = terminalId;
    this.onRequestPayment();
    this.successCallback = successCallback;
    if (onCloseCallback) this.onCloseCallback = onCloseCallback;
  }

  @action.bound
  async onCancel(): Promise<void> {
    try {
      if (!this.terminalPaymentDto) {
        return;
      }

      this.loading = true;

      const req = new CancelTerminalCheckoutRequest({
        terminalPayment: this.terminalPaymentDto
      });
      await this.client.post(req);

      runInAction(() => {
        this.checkoutId = null;
        this.loading = false;
        this.dialogOpen = false;
        this.onClear();
      });
    } catch (error) {
      runInAction(() => {
        this.printError('onCancel', 'An error has occured', error);
      });
    }
  }

  @action.bound
  async onRequestPayment(): Promise<void> {
    this.paymentError = false;
    this.errorMessage = '';

    this.terminalPaymentSuccessful = false;
    window.addEventListener('beforeunload', this.beforeUnloadWarning);

    const req = new StartTerminalPaymentRequest({
      amount: this.transactionAmount,
      terminalId: this.selectedTerminalId!,
      customerId: this.customerId,
      locationId: this.locationId !== -1 ? this.locationId : AuthStore.user?.location?.id,
      receivedAtLocationId: AuthStore.user?.location?.id
    });

    try {
      this.registerServerEventsClient();
      this.terminalPaymentDto = (await this.client.post(req)).result;

      switch (this.terminalPaymentDto?.terminalStatus) {
        case TerminalPaymentProviderPaymentStatus.InProgress:
          this.onRequestPaymentInProgress();
          break;

        case TerminalPaymentProviderPaymentStatus.Cancelled:
        case TerminalPaymentProviderPaymentStatus.Complete:
        case TerminalPaymentProviderPaymentStatus.Error:
        case TerminalPaymentProviderPaymentStatus.None:
        case undefined:
          this.onRequestPaymentNotInProgress();
          break;

        default:
          this.printError(
            'onRequestPayment',
            'Unknown status',
            this.terminalPaymentDto?.terminalStatus
          );
          break;
      }
    } catch {
      runInAction(() => {
        this.paymentError = true;
        this.errorMessage =
          'An error has occurred while attempting to send the payment to the selected terminal. Please Retry or Cancel below';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  @action.bound
  protected onRequestPaymentNotInProgress(): void {
    this.paymentError = true;
    this.errorMessage = _.startCase(this.terminalPaymentDto?.message);
    this.serverEventsClient.stop();
    window.clearInterval(this.interval);
    window.clearTimeout(this.failTimeout);
  }

  @action.bound
  protected onRequestPaymentInProgress(): void {
    window.clearInterval(this.interval);
    this.interval = window.setInterval(
      this.checkPaymentStatus,
      TerminalPaymentDialogStore.POLLING_INTERVAL
    );

    window.clearTimeout(this.failTimeout);
    this.failTimeout = window.setTimeout(this.failPayment, TerminalPaymentDialogStore.FAIL_TIMER);
  }

  @action.bound
  async registerServerEventsClient() {
    if (this.serverEventsClient) {
      await this.serverEventsClient.stop();
    }
    this.isProcessing = true;

    const clientId = LicenseObservable.license.get('clientID');
    const channels = [`${clientId}/channels/terminal/${this.selectedTerminalId}`];
    this.serverEventsClient = new ServerEventsClient('/api/', channels, {
      handlers: {
        TerminalUpdateMessage: (msg: TerminalUpdateMessage) => {
          // guard for weird webhook retries etc
          if (msg.merchantReference != this.terminalPaymentDto?.merchantReference) return;

          // don't poll if we've had a message recently...
          window.clearInterval(this.interval);
          this.interval = window.setInterval(
            this.checkPaymentStatus,
            TerminalPaymentDialogStore.POLLING_INTERVAL
          );

          runInAction(() => {
            if (msg.message == 'Payment Complete') {
              this.successCallback?.(this.terminalPaymentDto!);
              this.terminalPaymentSuccessful = true;
              this.dialogOpen = false;
              this.onClear();
            }
            this.processingStatusMessage = msg.message;
          });
        }
      }
    }).start();
  }

  @action.bound
  onTerminalChange(e): void {
    this.selectedTerminalId = cache.isClick ? e : e.target.value;
  }

  @action.bound
  async onClear(forRetry: boolean = false): Promise<void> {
    window.clearInterval(this.interval);
    window.clearTimeout(this.failTimeout);

    this.interval = undefined;
    this.failTimeout = undefined;
    this.paymentError = false;
    this.errorMessage = '';
    this.terminalPaymentDto = undefined;
    this.showUnloadWarning = false;

    if (!forRetry) {
      this.transactionAmount = 0;
      this.selectedTerminalId = '';
      this.isProcessing = false;
    }

    this.processingStatusMessage = 'Starting Communication with Terminal';
    window.removeEventListener('beforeunload', this.beforeUnloadWarning);

    if (this.serverEventsClient) {
      await this.serverEventsClient.stop();
    }

    if (this.onCloseCallback) this.onCloseCallback?.();
  }

  @action.bound
  async onRetry(): Promise<void> {
    this.onClear(true);
    await this.onRequestPayment();
  }

  @action.bound
  private async checkPaymentStatus(): Promise<void> {
    if (!this.terminalPaymentDto) return;

    const req = new TerminalPaymentStatusRequest({ terminalPayment: this.terminalPaymentDto });

    try {
      const { result } = await this.client.post(req);

      switch (result.terminalStatus) {
        case TerminalPaymentProviderPaymentStatus.Complete:
          if (result.status === PaymentProviderPaymentStatus.Success) {
            this.checkPaymentStatusCompleteSuccess(result);
            break;
          }
          this.checkPaymentStatusCompleteNotSuccess(result);
          break;

        case TerminalPaymentProviderPaymentStatus.Cancelled:
          this.checkPaymentStatusCancelled(result);
          break;

        case TerminalPaymentProviderPaymentStatus.Error:
          this.checkPaymentStatusErrored(result);
          break;

        case TerminalPaymentProviderPaymentStatus.InProgress:
          break;

        case TerminalPaymentProviderPaymentStatus.None:
          this.printError('checkPaymentStatus', 'Illegal status', result.terminalStatus);
          break;

        default:
          this.printError('checkPaymentStatus', 'Unknown status', result.terminalStatus);
          break;
      }
    } catch (err) {
      runInAction(() => {
        this.printError('checkPaymentStatus', 'TerminalPaymentStatusRequest error', err);
        window.clearInterval(this.interval);
        this.paymentError = true;
      });
    }
  }

  protected printError(methodName: string, message: string, ...args: any[]): void {
    console.error(`[TerminalPaymentDialogStore.${methodName}] - ${message}:`, ...args);
  }

  @action.bound
  protected checkPaymentStatusCompleteSuccess(result: TerminalPaymentDto): void {
    if (!!this.terminalPaymentDto) {
      const { incomeId } = result;

      if (incomeId > 0) this.terminalPaymentDto.incomeId = incomeId;

      this.successCallback?.(this.terminalPaymentDto);
    }

    this.onClear();
    window.removeEventListener('beforeunload', this.beforeUnloadWarning);
    this.terminalPaymentSuccessful = true;
  }

  @action.bound
  protected checkPaymentStatusCompleteNotSuccess(result: TerminalPaymentDto): void {
    window.clearInterval(this.interval);
    window.clearTimeout(this.failTimeout);

    this.paymentError = true;

    if (!!result.message) {
      this.errorMessage = _.startCase(result.message) || 'An error occurred taking the payment';
    }

    window.removeEventListener('beforeunload', this.beforeUnloadWarning);
  }

  @action.bound
  protected checkPaymentStatusCancelled(result: TerminalPaymentDto): void {
    window.clearInterval(this.interval);
    window.clearTimeout(this.failTimeout);

    this.paymentError = true;
    this.errorMessage = 'The payment was cancelled';
    window.removeEventListener('beforeunload', this.beforeUnloadWarning);
  }

  @action.bound
  protected checkPaymentStatusErrored(result: TerminalPaymentDto): void {
    window.clearInterval(this.interval);
    window.clearTimeout(this.failTimeout);

    const { message } = result;
    this.paymentError = true;
    this.errorMessage = message ?? 'An error occurred taking the payment';
  }

  @action.bound
  private async failPayment(): Promise<void> {
    const req = new CancelTerminalCheckoutRequest({
      terminalPayment: this.terminalPaymentDto
    });

    try {
      let res = await this.client.post(req);
    } finally {
      runInAction(() => {
        this.errorMessage =
          'The time to complete the checkout has expired, Please Retry or Cancel below';
        this.paymentError = true;
      });
    }
  }

  @action.bound
  closeDialog(): void {
    this.dialogOpen = false;
    this.onClear();
  }

  @action.bound
  async onCloseCancel(): Promise<void> {
    this.loading = true;
    await this.checkPaymentStatus();
    return this.terminalPaymentSuccessful ? this.closeDialog() : await this.onCancel();
  }
}

export const [TerminalPaymentDialogStoreCtx, useTerminalPaymentDialogStore] = createContextForClass(
  TerminalPaymentDialogStore
);

export default TerminalPaymentDialogStore;
